import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Grid,
  Box,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import Dropzone from 'react-dropzone';
import { validateSelectedImageFile } from '../../utils/file-validations.util';
import {
  downloadVideoAsset,
  uploadImageToBunnyCDN,
} from '../../services/content.service';
import Iconify from '../common/iconify.component';

const ImageUpload = ({
  title,
  dataId,
  showToastMsg,
  videoData,
  loading,
  showUpdatedImage,
}) => {
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFilesObj, setSelectedFilesObj] = useState(null);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [showUploading, setShowUploading] = useState(false);

  const handleDownloadFile = () => {
    const paramsQuery = '?type=THUMBNAIL';
    downloadVideoAsset(dataId, paramsQuery)
      .then((res) => {
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);

        let fileName = '';
        const disposition = res.headers['content-disposition'];
        if (disposition) {
          const match = disposition.match(/filename="?(.+?)"?$/);
          if (match) {
            [, fileName] = match;
          }
        }

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      })
      .catch((e) => {
        showToastMsg('error', e.response?.data?.message || 'Download failed');
      });
  };

  const handleSelectFiles = async (files) => {
    showToastMsg();
    showUpdatedImage(false);
    setSelectedFilesObj(files);
    setBtnDisabled(false);

    const validationError = validateSelectedImageFile(files);
    if (validationError) {
      showToastMsg('error', validationError);
    }

    // Show file name below button
    for (let i = 0; i < files.length; i += 1) {
      setSelectedFileName(files[i].name);
    }
  };

  const handleUploadFiles = async (videoId, selectedImageFileName) => {
    showToastMsg();

    const validationError = validateSelectedImageFile(selectedFilesObj);
    if (validationError) {
      showToastMsg('error', validationError);
    } else {
      setBtnDisabled(true);
      setShowUploading(true);
      const imageFormData = new FormData();
      imageFormData.append('videoId', videoId);
      imageFormData.append('file', selectedFilesObj[0]);
      imageFormData.append('imageType', title);
      imageFormData.append('selectedImageFileName', selectedImageFileName);
      try {
        const cdnResponse = await uploadImageToBunnyCDN(imageFormData);
        if (cdnResponse.data?.success) {
          showUpdatedImage(true);
          setSelectedFilesObj(null);
          setBtnDisabled(false);
          setSelectedFileName('');
          showToastMsg('success', `${title} Uploaded Successfully!`);
        } else {
          showToastMsg('error', 'Something went wrong! Please try again.');
        }
      } catch {
        showToastMsg('error', 'Something went wrong! Please try again.');
      }

      setShowUploading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        sm={4}
        md={4}
        sx={{
          whiteSpace: 'unset',
          wordBreak: 'break-all',
        }}
      >
        <Typography variant="button" display="block">
          {`${title}:`}
        </Typography>
        <Grid item xs={12} sm={6} md={6}>
          {loading && (
            <Box sx={{ display: 'flex' }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && (
            <div style={{ width: 150 }}>
              {videoData.thumbnail && title === 'Thumbnail' && (
                <img
                  src={videoData.thumbnail}
                  alt={title}
                  loading="lazy"
                  style={{
                    width: '100%',
                    aspectRatio: '16 / 9',
                    objectFit: 'cover',
                  }}
                />
              )}
              {videoData.heroImage && title === 'Hero Image' && (
                <img
                  src={videoData.heroImage}
                  alt={title}
                  loading="lazy"
                  style={{
                    width: '100%',
                    aspectRatio: '16 / 9',
                    objectFit: 'cover',
                  }}
                />
              )}
            </div>
          )}
        </Grid>

        <Typography variant="button" display="block" mt={1}>
          {`${title} URL:`}
          <IconButton
            aria-label="download"
            size="small"
            onClick={() => handleDownloadFile()}
          >
            <Iconify icon="ic:round-download" />
          </IconButton>
        </Typography>
        <Typography variant="caption">
          {title === 'Thumbnail' ? videoData.thumbnail : videoData.heroImage}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={8} md={8}>
        <Grid item xs={12} sm={12} md={12}>
          <Dropzone
            onDrop={(acceptedFiles) => handleSelectFiles(acceptedFiles)}
            disabled={showUploading}
          >
            {({ getRootProps, getInputProps }) => (
              <section>
                <div {...getRootProps()}>
                  <input {...getInputProps()} />

                  <Box
                    component="section"
                    sx={{
                      p: 1,
                      border: '1px dashed grey',
                      borderRadius: '20px',
                      width: '100%',
                      marginTop: 1,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={12}>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                          }}
                        >
                          <Iconify
                            icon="mdi:cloud-upload-outline"
                            width={42}
                            sx={{ color: 'text.secondary' }}
                          />
                          Drag and drop file here, or click the button below
                        </Grid>

                        <Box
                          display="flex"
                          justifyContent="center"
                          color="primary.main"
                          mt={1}
                        >
                          <Typography variant="body2" display="block">
                            {selectedFileName}
                          </Typography>
                        </Box>

                        <Box display="flex" justifyContent="center" p={1}>
                          <Button
                            component="label"
                            variant="contained"
                            sx={{
                              backgroundColor: '#808080',
                            }}
                            size="small"
                            disabled={showUploading}
                          >
                            {`Browse ${title}`}
                          </Button>
                        </Box>

                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="caption" display="block">
                            Supported formats: JPG, JPEG, PNG, GIF
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                </div>
              </section>
            )}
          </Dropzone>
        </Grid>

        <Box display="flex" justifyContent="center" p={1}>
          {!showUploading ? (
            <Button
              color="primary"
              variant="contained"
              size="small"
              disabled={btnDisabled}
              onClick={() => handleUploadFiles(dataId, selectedFileName)}
              startIcon={<Iconify icon="mdi:cloud-upload" />}
            >
              {`Upload ${title}`}
            </Button>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              padding="4px 10px"
              borderRadius="6px"
              sx={{
                backgroundColor: '#919eab3d',
                color: '#919eabcc',
              }}
              fontSize={14}
            >
              <CircularProgress
                disableShrink
                size="14px"
                sx={{ marginRight: 1, color: '#919eabcc' }}
              />
              <span>Uploading...</span>
            </Box>
          )}
        </Box>

        <Box display="flex" justifyContent="center">
          <Typography variant="caption">
            <b>Note:</b>&nbsp;Required resolution 16:9
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

ImageUpload.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  showToastMsg: PropTypes.func.isRequired,
  videoData: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  showUpdatedImage: PropTypes.func.isRequired,
};

export default ImageUpload;
